import { Controller } from "@hotwired/stimulus";

/**
 * Generic combobox controller for single- or multi-select with search filtering.
 *
 * Pairs with Knox::ComboboxComponent. Options are pre-rendered server-side;
 * this controller handles filtering, selection, keyboard navigation, and
 * ARIA state. Tags and hidden fields are stamped from <template> elements
 * so no HTML is built in JavaScript.
 */
export default class extends Controller {
  static targets = [
    "selectedTags",
    "searchInput",
    "dropdown",
    "hiddenFields",
    "option",
    "section",
    "noResults",
    "tagTemplate",
    "hiddenFieldTemplate",
  ];

  static values = {
    fieldName: String,
    placeholder: String,
    multiple: { type: Boolean, default: true },
    required: { type: Boolean, default: false },
  };

  connect() {
    this.selectedValues = new Set();
    this.highlightedIndex = -1;
    this._open = false;

    // Initialize selection state from pre-rendered hidden fields
    this.hiddenFieldsTarget
      .querySelectorAll(`input[name="${this.fieldNameValue}"]`)
      .forEach((input) => {
        this.selectedValues.add(input.value);
      });

    this._handleClickOutside = (event) => this.handleClickOutside(event);
    document.addEventListener("click", this._handleClickOutside);

    this.updateValidity();
  }

  /**
   * The selection lives in hidden inputs, which the browser excludes from
   * constraint validation, so `required` is enforced by putting a custom
   * validity message on the visible search input instead. That also gives the
   * validation bubble something on screen to anchor to.
   */
  updateValidity() {
    if (!this.requiredValue) return;

    this.searchInputTarget.setCustomValidity(
      this.selectedValues.size === 0 ? "Select at least one option." : "",
    );
  }

  disconnect() {
    this.cleanup();
  }

  // Called by Turbo before caching the page — ensures listeners don't leak
  teardown() {
    this.cleanup();
  }

  cleanup() {
    document.removeEventListener("click", this._handleClickOutside);
  }

  // -- Open / Close --

  get isOpen() {
    return this._open;
  }

  open() {
    this._open = true;
    this.dropdownTarget.style.display = "block";
    this.positionDropdown();
    this.searchInputTarget.setAttribute("aria-expanded", "true");
    this.searchInputTarget.focus();
  }

  close() {
    this._open = false;
    this.dropdownTarget.style.display = "none";
    this.dropdownTarget.classList.remove("knox-listbox__dropdown--above");
    this.searchInputTarget.setAttribute("aria-expanded", "false");
    this.searchInputTarget.removeAttribute("aria-activedescendant");
    this.searchInputTarget.value = "";
    this.highlightedIndex = -1;
    this.filterOptions("");
  }

  /**
   * Flip the list above the field when there isn't room for it below. Without
   * this, a combobox near the bottom of a scrolling page opens its results
   * off-screen and looks like it did nothing.
   */
  positionDropdown() {
    const field = this.element.getBoundingClientRect();
    const needed = this.dropdownTarget.offsetHeight;
    const roomBelow = window.innerHeight - field.bottom;
    const roomAbove = field.top;

    this.dropdownTarget.classList.toggle(
      "knox-listbox__dropdown--above",
      roomBelow < needed && roomAbove > roomBelow,
    );
  }

  // -- Search / Filter --

  search() {
    const term = this.searchInputTarget.value;
    this.highlightedIndex = -1;

    if (!this.isOpen) {
      this.open();
    }

    this.filterOptions(term);
    // Hiding options changes the list's height, which can change which side
    // of the field it fits on.
    this.positionDropdown();
  }

  filterOptions(term) {
    const normalizedTerm = term.toLowerCase().trim();
    let visibleCount = 0;

    this.optionTargets.forEach((option) => {
      // Collect all data-search-* attributes for generic matching
      const searchTerms = this.getSearchTerms(option);
      const matches =
        !normalizedTerm || searchTerms.some((t) => t.includes(normalizedTerm));

      option.hidden = !matches;
      if (matches) visibleCount++;
    });

    // Show/hide section headers based on whether they have visible items
    this.sectionTargets.forEach((section) => {
      const visibleOptions = section.querySelectorAll(
        ".knox-listbox__option:not([hidden])",
      );
      section.hidden = visibleOptions.length === 0;
    });

    // Show/hide empty state
    this.noResultsTarget.hidden = visibleCount > 0;

    this.updateHighlight();
  }

  /**
   * Reads all data-search-* attributes from an option element and returns
   * their lowercase values. This makes filtering generic -- the view decides
   * what fields are searchable by adding data-search-* attributes.
   */
  getSearchTerms(option) {
    return Object.keys(option.dataset)
      .filter((k) => k.startsWith("search"))
      .map((k) => option.dataset[k].toLowerCase());
  }

  // -- Selection --

  toggleItem(event) {
    const option = event.currentTarget;
    const value = option.dataset.value;

    if (this.selectedValues.has(value)) {
      this.deselect(option);
    } else {
      if (!this.multipleValue) this.deselectAll();
      this.select(option);
    }

    this.updateTags();
    this.updateHiddenFields();

    if (!this.multipleValue) {
      this.close();
      return;
    }

    // Clear search and refocus
    this.searchInputTarget.value = "";
    this.filterOptions("");
    this.searchInputTarget.focus();
  }

  removeTag(event) {
    event.stopPropagation();
    const value = event.currentTarget.dataset.value;

    const option = this.optionTargets.find(
      (opt) => opt.dataset.value === value,
    );
    if (option) {
      this.deselect(option);
    } else {
      this.selectedValues.delete(value);
    }

    this.updateTags();
    this.updateHiddenFields();
  }

  select(option) {
    this.selectedValues.add(option.dataset.value);
    option.classList.add("knox-listbox__option--selected");
    option.setAttribute("aria-selected", "true");
  }

  deselect(option) {
    this.selectedValues.delete(option.dataset.value);
    option.classList.remove("knox-listbox__option--selected");
    option.setAttribute("aria-selected", "false");
  }

  deselectAll() {
    this.optionTargets.forEach((option) => this.deselect(option));
  }

  // -- Tags (cloned from <template>) --

  updateTags() {
    const container = this.selectedTagsTarget;
    container.innerHTML = "";

    // Build tags from selected options, preserving display order
    this.optionTargets.forEach((option) => {
      if (this.selectedValues.has(option.dataset.value)) {
        const tag = this.tagTemplateTarget.content.cloneNode(true);
        const span = tag.querySelector(".knox-combobox__tag");
        const icon = tag.querySelector('[data-role="icon"]');
        const label = tag.querySelector('[data-role="label"]');
        const remove = tag.querySelector(".knox-combobox__tag-remove");

        span.dataset.value = option.dataset.value;

        if (icon && option.dataset.icon) {
          icon.src = option.dataset.icon;
        } else if (icon) {
          icon.remove();
        }

        if (label) {
          label.textContent = option.dataset.label;
        }

        if (remove) {
          remove.dataset.value = option.dataset.value;
          remove.setAttribute("aria-label", `Remove ${option.dataset.label}`);
        }

        container.appendChild(tag);
      }
    });

    this.updatePlaceholder();
  }

  updatePlaceholder() {
    this.searchInputTarget.placeholder =
      this.selectedValues.size === 0 ? this.placeholderValue : "";
  }

  // -- Hidden fields (cloned from <template>) --

  updateHiddenFields() {
    const container = this.hiddenFieldsTarget;
    container.innerHTML = "";

    this.selectedValues.forEach((value) => {
      const field = this.hiddenFieldTemplateTarget.content.cloneNode(true);
      const input = field.querySelector("input");
      input.value = value;
      container.appendChild(field);
    });

    // Both selection paths funnel through here, so validity stays in step.
    this.updateValidity();
  }

  // -- Keyboard navigation --

  handleKeydown(event) {
    const visibleOptions = this.optionTargets.filter((opt) => !opt.hidden);

    if (visibleOptions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.highlightedIndex = Math.min(
          this.highlightedIndex + 1,
          visibleOptions.length - 1,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
      case "Home":
        event.preventDefault();
        this.highlightedIndex = 0;
        break;
      case "End":
        event.preventDefault();
        this.highlightedIndex = visibleOptions.length - 1;
        break;
      case "Enter":
        event.preventDefault();
        if (
          this.highlightedIndex >= 0 &&
          this.highlightedIndex < visibleOptions.length
        ) {
          visibleOptions[this.highlightedIndex].click();
        }
        return;
      default:
        return;
    }

    this.updateHighlight();
    this.scrollToHighlighted();
  }

  updateHighlight() {
    const visibleOptions = this.optionTargets.filter((opt) => !opt.hidden);

    // Remove highlight from all options
    this.optionTargets.forEach((opt) =>
      opt.classList.remove("knox-listbox__option--highlighted"),
    );

    if (
      this.highlightedIndex >= 0 &&
      this.highlightedIndex < visibleOptions.length
    ) {
      const highlighted = visibleOptions[this.highlightedIndex];
      highlighted.classList.add("knox-listbox__option--highlighted");

      // Update aria-activedescendant to point to the highlighted option
      this.searchInputTarget.setAttribute(
        "aria-activedescendant",
        highlighted.id,
      );
    } else {
      this.searchInputTarget.removeAttribute("aria-activedescendant");
    }
  }

  scrollToHighlighted() {
    const highlighted = this.element.querySelector(
      ".knox-listbox__option--highlighted",
    );
    if (highlighted) {
      highlighted.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  // Escape should close the combobox if it is open, and prevent propagation of the event to other elements
  handleKeyup(event) {
    if (event.key === "Escape" && this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  }

  // -- Click outside --

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }
}
